// Lume (contracts/lume.md): the line library's triggers, moods and placeholders, the lume.* config keys as
// backend/api validates them, and helpers for the Automation and Ask Lume pages. Shared by the pages and the mock
// (src/mocks/db.ts validates PUT /config/lume.* with these schemas; src/mocks/lume.ts checks lines with them).

import { z } from "zod";

// ---------------------------------------------------------------- moods

/** The app's Lume mood set (design/mascot/README.md); the PNGs are in public/lume/. */
export const MOODS = ["idle", "think", "cheer", "celebrate", "muted", "sleepy", "wave", "nudge"] as const;
export type Mood = (typeof MOODS)[number];
export const MOOD_LABEL: Record<Mood, string> = {
  idle: "Idle · at rest",
  think: "Think · solving",
  cheer: "Cheer · a correct answer",
  celebrate: "Celebrate · a top result",
  muted: "Muted · a lost streak",
  sleepy: "Sleepy · late or offline",
  wave: "Wave · hello",
  nudge: "Nudge · reminders",
};
export const moodSrc = (mood: Mood) => `${import.meta.env.BASE_URL}lume/lume-${mood}.png`;

// ---------------------------------------------------------------- triggers

export const LINE_CHANNELS = ["push", "app"] as const;
export type LineChannel = (typeof LINE_CHANNELS)[number];

export interface TriggerInfo {
  channel: LineChannel;
  description: string;
  placeholders: string[];
}

/** backend/api services/lume.ts TRIGGER_INFO (the API sends the same list as `triggers`; the mock uses this one). */
export const TRIGGER_INFO: Record<string, TriggerInfo> = {
  comeback_2d: { channel: "push", description: "Not seen for 2+ days (local 18:00–20:00), once per absence.", placeholders: ["days"] },
  comeback_5d: { channel: "push", description: "Not seen for 5+ days, once per absence.", placeholders: ["days"] },
  comeback_12d: { channel: "push", description: "Not seen for 12+ days, once per absence.", placeholders: ["days"] },
  review_due: { channel: "push", description: "3+ Review items due; at most every 3 days (local 17:00–19:00). 1.1+.", placeholders: ["count"] },
  focus_week: { channel: "push", description: "No Focus for 7 days but played a daily; at most weekly (local 17:00–19:00). 1.1+.", placeholders: ["category"] },
  open_morning: { channel: "app", description: "App open, 05–11 local.", placeholders: [] },
  open_afternoon: { channel: "app", description: "App open, 11–17 local.", placeholders: [] },
  open_evening: { channel: "app", description: "App open, 17–22 local.", placeholders: [] },
  open_night: { channel: "app", description: "App open, 22–05 local.", placeholders: [] },
  open_comeback: { channel: "app", description: "App open after 2+ days away.", placeholders: ["days"] },
  open_streak: { channel: "app", description: "App open with a live streak of 3+.", placeholders: ["streak"] },
  open_new: { channel: "app", description: "App open with an unread announcement or an unseen story.", placeholders: [] },
  after_win: { channel: "app", description: "After a solved level, Focus or Review puzzle.", placeholders: [] },
  after_miss: { channel: "app", description: "After a missed level, Focus or Review puzzle.", placeholders: [] },
  hint_nudge: { channel: "app", description: "No answer for 45 s, or 2 misses: a technique nudge, never the answer.", placeholders: [] },
  cheer_run: { channel: "app", description: "3 correct in a row.", placeholders: [] },
  idle: { channel: "app", description: "Home for 20 s without a tap.", placeholders: [] },
  daily_reminder: { channel: "app", description: "The local daily reminder; the app schedules 7 days with different lines.", placeholders: ["streak"] },
  chat_greeting: { channel: "app", description: "The first bubble of Ask Lume.", placeholders: [] },
};

/** The lume.pushTriggers switch each push trigger answers to (the three come-backs share one). */
export const PUSH_SWITCH: Record<string, keyof LumePushTriggers> = {
  comeback_2d: "comeback",
  comeback_5d: "comeback",
  comeback_12d: "comeback",
  review_due: "review_due",
  focus_week: "focus_week",
};

export const TRIGGER_LABEL: Record<string, string> = {
  comeback_2d: "Come back · 2 days",
  comeback_5d: "Come back · 5 days",
  comeback_12d: "Come back · 12 days",
  review_due: "Review due",
  focus_week: "Focus this week",
  open_morning: "Open · morning",
  open_afternoon: "Open · afternoon",
  open_evening: "Open · evening",
  open_night: "Open · night",
  open_comeback: "Open · back after days away",
  open_streak: "Open · on a streak",
  open_new: "Open · something new",
  after_win: "After a win",
  after_miss: "After a miss",
  hint_nudge: "Hint nudge",
  cheer_run: "Cheer a run",
  idle: "Idle on Home",
  daily_reminder: "Daily reminder",
  chat_greeting: "Ask Lume greeting",
};
export const triggerLabel = (t: string) => TRIGGER_LABEL[t] ?? t;

/** Line limits (contracts/lume.md §1). */
export const LINE_LIMITS = { title: 50, text: 160 } as const;
/** In-app triggers want variety: the app never repeats one of the last 5 lines it showed. */
export const APP_MIN_LINES = 5;

/** Values the preview fills in, the way the server (pushes) or the app (in-app lines) would. */
export const SAMPLE_VALUES: Record<string, string> = { streak: "12", days: "5", count: "4", category: "Pattern" };

const PLACEHOLDER = /\{([a-z]+)\}/g;

/** The `{name}` placeholders a text uses. */
export const placeholdersIn = (text: string) => [...text.matchAll(PLACEHOLDER)].map((m) => m[1]!);

/** Placeholders the trigger doesn't allow (the API answers 400 for these). */
export const unknownPlaceholders = (text: string, allowed: readonly string[]) => [...new Set(placeholdersIn(text).filter((p) => !allowed.includes(p)))];

/** The text with sample values, as a player would read it. */
export const fillSample = (text: string) => text.replace(PLACEHOLDER, (all, k: string) => SAMPLE_VALUES[k] ?? all);

/** The API's 400 for a placeholder the trigger doesn't allow. */
export const placeholderMessage = (name: string, trigger: string, locale: string, allowed: readonly string[]) =>
  `{${name}} isn't available for ${trigger} (${locale}); allowed: ${allowed.map((p) => `{${p}}`).join(" ") || "none"}.`;

// ---------------------------------------------------------------- config (backend/api services/appConfig.ts)

const LumePushTriggers = z.strictObject({ comeback: z.boolean(), review_due: z.boolean(), focus_week: z.boolean() });
export type LumePushTriggers = z.infer<typeof LumePushTriggers>;
const LumeChat = z.strictObject({
  enabled: z.boolean(),
  freeDaily: z.int().min(0).max(1000),
  proDaily: z.int().min(0).max(1000),
  model: z.string().min(1).max(64),
  maxOutputTokens: z.int().min(50).max(4000),
  monthlyBudgetUsd: z.number().min(0).max(100_000),
});
export type LumeChatConfig = z.infer<typeof LumeChat>;

export const LUME_CONFIG = {
  "lume.pushTriggers": { schema: LumePushTriggers, default: { comeback: true, review_due: true, focus_week: true } as LumePushTriggers },
  "lume.chat": {
    schema: LumeChat,
    default: { enabled: true, freeDaily: 5, proDaily: 50, model: "claude-haiku-4-5", maxOutputTokens: 400, monthlyBudgetUsd: 50 } as LumeChatConfig,
  },
} as const;

/** The lume.* values from GET /config, each falling back to its default when missing or unreadable. */
export function readLumeConfig(values: Record<string, unknown> | undefined): { pushTriggers: LumePushTriggers; chat: LumeChatConfig } {
  const push = LUME_CONFIG["lume.pushTriggers"].schema.safeParse(values?.["lume.pushTriggers"]);
  const chat = LUME_CONFIG["lume.chat"].schema.safeParse(values?.["lume.chat"]);
  return {
    pushTriggers: push.success ? push.data : LUME_CONFIG["lume.pushTriggers"].default,
    chat: chat.success ? chat.data : LUME_CONFIG["lume.chat"].default,
  };
}

/** Why a chat setting can't be saved, or null (the same bounds as the API). */
export function chatProblem(c: LumeChatConfig, models: readonly string[]): string | null {
  const int = (v: number, min: number, max: number) => Number.isInteger(v) && v >= min && v <= max;
  if (!int(c.freeDaily, 0, 1000)) return "Free questions per day: a whole number from 0 to 1000.";
  if (!int(c.proDaily, 0, 1000)) return "Pro questions per day: a whole number from 0 to 1000.";
  if (!int(c.maxOutputTokens, 50, 4000)) return "Max reply tokens: a whole number from 50 to 4000.";
  if (!(c.monthlyBudgetUsd >= 0 && c.monthlyBudgetUsd <= 100_000)) return "Monthly budget: from $0 to $100,000.";
  if (!models.includes(c.model)) return "Pick a model with a price in studio.prices.";
  return null;
}
